import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { BrowserSupport } from '../../common/utilities/BrowserSupport';

class Normalizer {

  private n: number;
  private max: number;

  constructor(values: number[], private width: number, private height: number,
      private originX: number = 0, private originY: number = 0) {
    this.n = values.length - 1;
    this.max = _.max(values);
  }

  public y(value: number) {
    // Because SVG's y axis starts with 0 at the top and increases downward, the normalization
    // also inverts the numbers on the axis so lower numbers appear lower on the screen.
    const y = (this.max === 0) ? this.height : (this.max - value) * this.height / this.max;
    return y + this.originY;
  }

  public x(i: number) {
    const x = (this.n === 0) ? this.width : i * this.width / this.n;
    return x + this.originX;
  }
}

class PathBuilder {

  constructor(private normalize: Normalizer) { }

  private buildMovement(name: string, x: number, y: number): string {
    return name + x + ',' + y;
  }

  public buildPath(dataPoints: number[]): string {
    return dataPoints.reduce((movements: string, value: number, i: number): string => {
      const moveType = (i === 0) ? 'M' : 'L';
      const x = this.normalize.x(i);
      const y = this.normalize.y(value);
      return movements + this.buildMovement(moveType, x, y);
    }, '');
  }
}

// Exported for the unit tests
export class OffsetCorrector {
  private boundRect: ClientRect;
  constructor(element: Element) {
    this.boundRect = element.getBoundingClientRect();
  }

  public correctHorizontalOffset(element: Element): number {
    const elementRect = element.getBoundingClientRect();
    const rightOverreach = elementRect.right - (this.boundRect.right - Sparkline.TextBuffer);
    const leftOverreach = (this.boundRect.left + Sparkline.TextBuffer) - elementRect.left;

    let n = 0;
    let totalCorrection = 0;
    if (rightOverreach > 0) {
      totalCorrection -= rightOverreach;
      n++;
    }
    if (leftOverreach > 0) {
      totalCorrection += leftOverreach;
      n++;
    }

    // Average the two corrections
    return n === 0 ? 0 : (totalCorrection / n);
  }
}

/**
 * A miniature line graph of the data points given in the props. The data points will be spread
 * evenly across the x axis, and the y axis will range from 0 to the value of the largest data point
 * (all data values are assumed to be non-negative).
 *
 * This component uses SVG. Browsers that do not support SVG elements in the DOM will get
 * undefined behavior if they try to render this component. Use the browserSupportForSparkline method to determine
 * if the component can be rendered.
 */
export class Sparkline extends React.Component<Sparkline.Props, Sparkline.State> {

  public static displayName = 'Sparkline';
  public static element = React.createFactory(Sparkline);

  public static browserSupportForSparkline(): boolean {
    return BrowserSupport.supportsSvgElements();
  }

  // Style properties of the sparkline.
  public static LineWidth = 2;
  public static LineColor = CommonStyles.Colors.Grey153;
  public static BackgroundColor = CommonStyles.Colors.Grey245;

  // The number of pixels by which a text should be separated from other elements
  public static TextBuffer = 3;

  // The amount of padding from the edge of the SVG to the edges of the path. Circles and text
  // might occupy this space, so the values must be greater than any of the circles' (r + strokeWidth).
  public static DefaultPadding = {
    paddingTop: 18,
    paddingRight: 12,
    paddingBottom: 18,
    paddingLeft: 12
  };

  public static CircleStyleEnd = {
    r: 3.5,
    strokeWidth: 0,
    fill: CommonStyles.Colors.Grey102
  };

  public static CircleStyleMax = {
    r: 2.5,
    strokeWidth: 1,
    fill: CommonStyles.Colors.White,
    stroke: CommonStyles.Colors.Grey102
  };

  public static CircleStyleMin = {
    r: 2.5,
    strokeWidth: 0,
    fill: CommonStyles.Colors.Grey136,
  };

  public static LabelStyle = {
    fontSize: CommonStyles.Sizes.SmallFontSize, // size must be in pixels
    fill: CommonStyles.Colors.GreyTextColor // SVG text color uses the 'fill' attribute
  };


  private normalize: Normalizer;

  constructor(props: Sparkline.Props) {
    super(props);
    this.state = this.defaultState(props);
  }

  private setStateForFullRender(){
    if (this.state.dataPointsToLabel && this.state.dataPointsToLabel.length > 0) {
      const labels = this.measureTextAndSetOffsets();
      this.setState({
        dataPointsToLabel: labels,
        readyForFullRender: true
      });
    }
  }

  /*
   * Measures each text label and makes sure it will render within the confines of the SVG element.
   */
  private measureTextAndSetOffsets() {

    const offsetCorrector = new OffsetCorrector(ReactDOM.findDOMNode(this.refs['svg']) as Element);

    return this.state.dataPointsToLabel.map((label: Sparkline.DataPointLabelSpec) => {
      // expects the labels to have been pushed onto the DOM by the first pass of rendering
      const textElement = ReactDOM.findDOMNode(this.refs[label.key + 'Label']) as Element;
      const newLabel = _.clone(label);
      newLabel.xOffset = offsetCorrector.correctHorizontalOffset(textElement);
      return newLabel;
    });
  }

  private defaultState(props: Sparkline.Props): Sparkline.State {
    return {
      readyForFullRender: false,
      dataPointsToLabel: this.getPointsToLabel(props.dataPoints),
    };
  }

  /*
   * When new props are received, the component must clear its state so that the two-pass render
   * can happen in full again.
   */
  public componentWillReceiveProps(nextProps: Sparkline.Props) {
    this.setState(this.defaultState(nextProps));
  }

  /*
   * As soon as the component has finished the first pass of the two-pass render,
   * perform the measurements and state change that will trigger the second pass.
   */
  public componentDidMount() {
    this.setStateForFullRender();
  }
  public componentDidUpdate() {
    if (!this.state.readyForFullRender) {
      this.setStateForFullRender();
    }
  }

  /*
   * This component renders itself in two passes. The first time, only the text labels are
   * rendered and the element is hidden. Then, on componentDidMount/compenentDidUpdate, the text
   * elements are measured, and the component computes the appropriate layout parameters to make
   * everything fits. Once this new state is set, the render happens again, this time visibly
   * rendering the entire component with the correct spacing.
   */
  public render(): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const fullRender = this.state.readyForFullRender;

    const svgStyle = {
      display: 'block',
      backgroundColor: Sparkline.BackgroundColor
    };

    const svgProps: React.SVGProps<SVGElement> = {
      ref: 'svg',
      height: this.props.height,
      width: this.props.width,
      style: svgStyle,
      'data-tb-test-id': 'sparkline'
    } as React.SVGProps<SVGElement>;

    const innerWidth = this.props.width - Sparkline.DefaultPadding.paddingLeft - Sparkline.DefaultPadding.paddingRight;
    const innerHeight = this.props.height - Sparkline.DefaultPadding.paddingTop - Sparkline.DefaultPadding.paddingBottom;
    const subcomponents: React.ReactNode[] = [];

    if (this.props.dataPoints && this.props.dataPoints.length > 0) {
      this.normalize = new Normalizer(this.props.dataPoints, innerWidth, innerHeight,
        Sparkline.DefaultPadding.paddingLeft, Sparkline.DefaultPadding.paddingTop);
      if (fullRender) {
        subcomponents.push(this.path());
      }

      this.state.dataPointsToLabel.forEach((spec: Sparkline.DataPointLabelSpec) => {
        subcomponents.push(this.labelAtDataPoint(spec)); //push to DOM for measureTextAndSetOffsets
        if (fullRender) {
          subcomponents.push(this.circleAtDataPoint(spec));
        }
      });
    }

    return React.createElement('svg', svgProps, ...subcomponents);
  }

  private path(): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const pathBuilder = new PathBuilder(this.normalize);

    return React.createElement('path', {
      key: 'path',
      stroke: Sparkline.LineColor,
      strokeWidth: Sparkline.LineWidth,
      fill: 'none',
      d: pathBuilder.buildPath(this.props.dataPoints)
    });
  }

  private circleAtDataPoint(spec: Sparkline.DataPointLabelSpec): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const fullName = spec.key + 'Circle';
    return React.createElement('circle', _.extend({
      key: fullName,
      'data-tb-test-id': fullName,
      cx: this.normalize.x(spec.index),
      cy: this.normalize.y(this.props.dataPoints[spec.index])
    }, spec.circleStyle));
  }

  /*
   * Puts a label at data point i, with the data point's value as the circle text.
   * If circleStyle is passed in, it is assumed that a circle exists on that data point with the given style.
   * above denotes the vertical position of the label with respect to the data point:
   *   true puts it above; false puts it below.
   */
  private labelAtDataPoint(spec: Sparkline.DataPointLabelSpec):
      React.ReactElement<React.SVGAttributes<SVGElement>> {
    const labelValue = this.props.dataPoints[spec.index];
    const fullName = spec.key + 'Label';

    const textBuffer = spec.circleStyle.r + spec.circleStyle.strokeWidth + Sparkline.TextBuffer;
    const yOffset = spec.above ? -textBuffer : textBuffer + Sparkline.LabelStyle.fontSize - 1.5;

    return React.createElement('text', {
      ref: fullName,
      key: fullName,
      'data-tb-test-id': fullName,
      style: this.state.readyForFullRender ? Sparkline.LabelStyle :
          _.defaults({visibility: 'hidden'}, Sparkline.LabelStyle),
      x: this.normalize.x(spec.index),
      y: this.normalize.y(labelValue),
      dy: yOffset,
      dx: spec.xOffset,
      textAnchor: 'middle',
    } as React.SVGProps<SVGElement>,
    labelValue);
  }

  private getPointsToLabel(dataPoints: number[]): Sparkline.DataPointLabelSpec[] {

    const labelPoints: Sparkline.DataPointLabelSpec[] = [];

    if (!dataPoints || dataPoints.length === 0) {
        return labelPoints;
    }

    const minIndex = _.indexOf(dataPoints, _.min(dataPoints));
    const maxIndex = _.indexOf(dataPoints, _.max(dataPoints));
    const endIndex = dataPoints.length - 1;
    const endPointHigher: boolean = dataPoints[endIndex] >= dataPoints[endIndex - 1];

    labelPoints.push({
      key: 'end',
      index: endIndex,
      circleStyle: Sparkline.CircleStyleEnd,
      above: endPointHigher});

    if (endIndex !== maxIndex) {
      labelPoints.push({
        key: 'max',
        index: maxIndex,
        circleStyle: Sparkline.CircleStyleMax,
        above: true
      });
    }

    if (endIndex !== minIndex && maxIndex !== minIndex) {
      labelPoints.push({
        key: 'min',
        index: minIndex,
        circleStyle: Sparkline.CircleStyleMin,
        above: false
      });
    }

    return labelPoints;
  }
}

export module Sparkline {
  export interface Props {

    // At least two data points are required for the sparkline to render correctly
    dataPoints: number[];

    // The pixel dimensions of the sparkline element, including any required padding.
    // Since SVG paths only support absolute coordinates, we need to know the size up front.
    width: number;
    height: number;
  }

  export interface State {
    readyForFullRender: boolean;
    dataPointsToLabel: DataPointLabelSpec[];
  }

  export interface DataPointLabelSpec {
    key: string;
    index: number;
    circleStyle: any;
    above: boolean;
    xOffset?: number; // x-axis offset in pixels
  }

}

/**
 * A wrapper around the Sparkline component that adds a title at the top and a caption below.
 */
export class SparklineWithTitle extends React.Component<SparklineWithTitle.Props, SparklineWithTitle.State> {
  public static displayName = 'SparklineWithTitle';
  public static element = React.createFactory(SparklineWithTitle);

  public static TitleStyle: React.CSSProperties = {
    fontSize: CommonStyles.Sizes.NormalFontSize,
    color: CommonStyles.Colors.NormalTextColor,
    marginBottom: 5,
    textAlign: 'left'
  };

  public static CaptionStyle: React.CSSProperties = {
    fontSize: CommonStyles.Sizes.SmallFontSize,
    color: CommonStyles.Colors.GreyTextColor,
    marginTop: 6,
    textAlign: 'center'
  };

  public render(): React.ReactElement<React.DOMAttributes<HTMLDivElement>> {

    const elements: React.ReactNode[] = [Sparkline.element(this.props.sparklineProps)];

    if (this.props.title) {
      const title = React.createElement('div',
        {
          style: SparklineWithTitle.TitleStyle,
          'data-tb-test-id': 'sparkline-title'
        } as React.HTMLAttributes<HTMLDivElement>,
        this.props.title);
      elements.unshift(title);
    }

    if (this.props.caption) {
      const caption = React.createElement('div',
        {
          style: SparklineWithTitle.CaptionStyle,
          'data-tb-test-id': 'sparkline-caption'
        } as React.HTMLAttributes<HTMLDivElement>,
        this.props.caption);
      elements.push(caption);
    }

    return React.createElement('div', {'data-tb-test-id': 'sparkline-with-title'}  as React.HTMLAttributes<HTMLDivElement>, ...elements);
  }
}

export module SparklineWithTitle {
  export interface Props {
    title?: string;
    caption?: string;
    sparklineProps: Sparkline.Props;
  }

  export interface State {}
}
